import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { GridRenderCellParams } from '@mui/x-data-grid';
import type { ReactNode } from 'react';
import { CreativeIdLine } from './CreativeIdLine';
import { CreativeScoreBadge } from './CreativeScoreBadge';
import { CreativeScorePopover } from './CreativeScorePopover';
import type { CreativeRow, CreativesListData, MetricCatalog, MetricTrendScope } from './data/api';
import { formatDelta, formatMetricValue, type MetricFormat } from './data/format';
import { MetricTrendPopover } from './MetricTrendPopover';
import { NetworkBadge } from './NetworkBadge';
import { ThumbnailPreview } from './ThumbnailPreview';
import { creativeSubline, deltaColor, ELLIPSIS_SX } from './utils';

export const TOTALS_ROW_ID = '__totals__';

const THUMB_SIZE = 66;

export interface CreativeGridRow {
  id: string;
  creative: CreativeRow | null;
  [key: string]: unknown;
}

export function toGridRow(creative: CreativeRow): CreativeGridRow {
  const row: CreativeGridRow = { id: creative.creativeId, creative };
  for (const [key, value] of Object.entries(creative.metrics)) row[`metric:${key}`] = value;
  for (const [key, value] of Object.entries(creative.uplift)) row[`uplift:${key}`] = value;
  return row;
}

export function totalsGridRow(totals: CreativesListData['totals'], count: number): CreativeGridRow {
  const row: CreativeGridRow = { id: TOTALS_ROW_ID, creative: null, count };
  for (const [key, value] of Object.entries(totals)) row[`metric:${key}`] = value;
  return row;
}

export function CreativeCell({ row }: GridRenderCellParams<CreativeGridRow>): ReactNode {
  if (row.creative === null) {
    return (
      <Typography variant="h5" sx={{ whiteSpace: 'nowrap' }}>
        {`Totals / averages · ${row['count']}`}
      </Typography>
    );
  }
  const creative = row.creative;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, minWidth: 0, height: '100%' }}>
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <ThumbnailPreview
          name={creative.creativeId}
          size={THUMB_SIZE}
          thumbnailUrl={creative.thumbnailUrl}
          previewUrl={creative.previewUrl}
          format={creative.format}
          duration={creative.duration}
        />
        <Box sx={{ position: 'absolute', top: 5, left: 5 }}>
          <NetworkBadge network={creative.network} padding={0.5} />
        </Box>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <CreativeIdLine creativeId={creative.creativeId} />
        <Tooltip title={creativeSubline(creative)} placement="bottom">
          <Typography color="text.secondary" sx={ELLIPSIS_SX}>
            {creativeSubline(creative)}
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );
}

export function creativeScoreCellRenderer(leadingKpiLabel: string) {
  return function CreativeScoreCell({ row }: GridRenderCellParams<CreativeGridRow>): ReactNode {
    if (row.creative === null) return null;
    const { creativeScore } = row.creative;
    if (creativeScore === undefined || creativeScore.score === null) {
      return <Typography color="text.disabled">—</Typography>;
    }
    return (
      <CreativeScorePopover creativeScore={creativeScore} leadingKpiLabel={leadingKpiLabel}>
        <CreativeScoreBadge creativeScore={creativeScore} />
      </CreativeScorePopover>
    );
  };
}

function fieldCell(select: (creative: CreativeRow) => number | null, format: MetricFormat) {
  return function FieldCell({ row }: GridRenderCellParams<CreativeGridRow>): ReactNode {
    if (row.creative === null) return null;
    return (
      <Typography
        variant="h5"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatMetricValue(select(row.creative), format, '')}
      </Typography>
    );
  };
}

export const ActiveDaysCell = fieldCell((creative) => creative.activeDays, 'number');
export const SpendRatioCell = fieldCell((creative) => creative.spendRatio, 'percentage');

export function metricCellRenderer(
  column: CreativesListData['columns'][number],
  catalog: MetricCatalog | undefined,
  currencySymbol: string,
  trendScope: Omit<MetricTrendScope, 'metric' | 'creativeId'>,
) {
  return function MetricCellRenderer({ row }: GridRenderCellParams<CreativeGridRow>): ReactNode {
    const value = (row[`metric:${column.name}`] as number | undefined) ?? null;
    if (row.creative === null) {
      return (
        <Typography
          variant="h5"
          sx={{ textAlign: 'right', width: '100%', fontVariantNumeric: 'tabular-nums' }}
        >
          {formatMetricValue(value, column.format, currencySymbol)}
        </Typography>
      );
    }
    const improvement = (row[`uplift:${column.name}`] as number | undefined) ?? null;
    const lowerIsBetter = catalog?.[column.name]?.lowerIsBetter === true;
    const change = improvement === null ? null : lowerIsBetter ? -improvement : improvement;
    return (
      <MetricTrendPopover
        scope={{ ...trendScope, creativeId: row.creative.creativeId }}
        metric={{ name: column.name, aggregation: 'total' }}
        currencySymbol={currencySymbol}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatMetricValue(value, column.format, currencySymbol)}
          </Typography>
          {change === null ? null : (
            <Typography
              variant="caption"
              component="p"
              sx={{
                fontVariantNumeric: 'tabular-nums',
                color: deltaColor(improvement, 'text.secondary'),
              }}
            >
              {formatDelta(change)}
            </Typography>
          )}
        </Box>
      </MetricTrendPopover>
    );
  };
}
